import dayjs from 'dayjs'
import classNames from 'classnames'
import { useShallow } from 'zustand/react/shallow'
import { ImdbIcon } from 'assets/icons'
import CountryFlag from 'components/CountryFlag'
import { optionsParser, phoneNumberParser, t } from 'helpers'
import { useAppStore, useUserStore } from 'store'
import { CUSTOMER_ROLE, User } from 'types/user'
import { basicFields, contactFields } from 'pages/Portfolio/fields'
import s from './index.module.scss'

interface Props {
	user: User | undefined
	agency: User | undefined
}

const Info = ({ user, agency }: Props): JSX.Element => {
	const director = useUserStore(
		useShallow(
			(state) =>
				state.user?.CustomerRole?.toLowerCase() ===
				CUSTOMER_ROLE.CASTING_DIRECTOR,
		),
	)
	const phoneNumber = agency ? '' : user?.CustomUserPhoneNumber
	const { countries } = useAppStore.getState()
	const country = countries?.find(
		(c) => c.name === user?.CustomerLocationCountry,
	)

	const phone = director ? phoneNumberParser(phoneNumber) : null

	const profession = optionsParser(
		t(basicFields?.profession?.optionName || ''),
	)?.find((v) => v.value === user?.CustomerProfession)?.label

	const infoValues = [
		profession,
		user?.CustomUserBirthDate
			? dayjs().diff(dayjs(user?.CustomUserBirthDate), 'year') + ' yr'
			: '',
	]
		?.filter((v) => !!v)
		?.join(', ')

	const imdb = user?.CustomerImdbUrl && user?.CustomerImdbUrl?.includes('imdb')
	const website = user?.[
		contactFields.website.name?.replaceAll(':', '') as keyof User
	] as string

	return (
		<div className={s.infoWrapper}>
			<div className={s.name}>
				<h3 className="h3-b">{user?.ContentTitle}</h3>
			</div>

			<div className={s.info}>
				<span className="body2-m" style={{ color: 'var(--mono300)' }}>
					{infoValues}
				</span>
			</div>
			<div className={s.info}>
				<CountryFlag code={country?.iso2} />
				<span className="body2-m" style={{ color: 'var(--mono300)' }}>
					{[user?.CustomerLocationCountry, user?.CustomerLocationCity]
						?.filter((v) => !!v)
						?.join(', ')}
				</span>
			</div>

			{phone ? (
				<a
					href={`tel:+${phone}`}
					className={classNames('body2-m', 'textEllipsis', s.contacts)}
					style={{ opacity: phone ? '1' : '0' }}>
					{'+' +
						phone?.slice(0, 2) +
						' ' +
						phone?.slice(2, 5) +
						' ' +
						phone?.slice(5, 7) +
						' ' +
						phone?.slice(7, 9) +
						' ' +
						phone?.slice(9)}
				</a>
			) : null}

			{!agency && user?.CustomUserEmail && director ? (
				<a
					href={`mailto:${user?.CustomUserEmail}`}
					className={classNames('body2-m', 'textEllipsis', s.contacts)}>
					{user?.CustomUserEmail}
				</a>
			) : null}
			{imdb ? (
				<a
					href={user?.CustomerImdbUrl}
					target="_blank"
					style={{ display: 'flex' }}>
					<ImdbIcon />
				</a>
			) : null}
			{website?.startsWith('http') ? (
				<a
					href={website}
					target="_blank"
					style={{ display: 'flex', color: 'var(--theme-primary)' }}>
					{website}
				</a>
			) : null}
		</div>
	)
}

export default Info
